import type { JourneyCondition } from '../journey/steps.ts'
import type { JourneyCheck } from '../journey/check-types.ts'
import { canonicalTarget, componentPaths } from '../polish/sightmap/targets.ts'
import { z } from 'zod'
import { flattenComponents, type SightmapDoc } from '../polish/sightmap/schema.ts'

export const COMPONENT_PATH = z.array(z.object({
  componentId: z.string().min(1), params: z.record(z.string()).optional(),
}).strict()).min(1)
export const SIGHTMAP_TARGET = z.object({ surfaceId: z.string().min(1), path: COMPONENT_PATH, selector: z.string().trim().min(1).optional(), paramsSource: z.literal('observation').optional(), observationRef: z.string().min(1).optional() }).strict()
// controlId is read compatibility for existing catalogs. New bindings carry the complete path.
export const COMPONENT_TARGET = z.object({
  surfaceId: z.string().min(1), controlId: z.string().min(1).optional(), path: COMPONENT_PATH.optional(), selector: z.string().trim().min(1).optional(), paramsSource: z.literal('observation').optional(), observationRef: z.string().min(1).optional(),
}).refine(target => !!target.path?.length || !!target.controlId, 'A Sightmap target needs a component path')
// Fresh conditions share the same complete targets as actions and checks. Persisted schemas
// still read selector conditions so conversion can bind historical evidence without dropping gates.
export const AUTHORED_CONDITION: z.ZodType<JourneyCondition> = z.lazy(() => z.union([
  z.object({ type: z.literal('route'), operator: z.enum(['matches', 'not_matches']), pattern: z.string() }).strict(),
  z.object({ type: z.literal('component'), operator: z.enum(['exists', 'not_exists']), component: SIGHTMAP_TARGET }).strict(),
  z.object({ type: z.enum(['all', 'any']), conditions: z.array(AUTHORED_CONDITION).min(1) }).strict(),
  z.object({ type: z.literal('not'), condition: AUTHORED_CONDITION }).strict(),
]))
export type ComponentTarget = z.infer<typeof COMPONENT_TARGET>
/** Observation handles are capabilities for one captured page, never shared tool identity. */
export function durableTarget(target: ComponentTarget): ComponentTarget {
  if (target.path?.some(part => /^observed-(?:control|text)-/.test(part.componentId))) {
    throw new Error('Temporary observation IDs cannot be stored; resolve the control within its containing component')
  }
  const { observationRef: _observationRef, ...durable } = target
  return durable
}
/** Stored-form target equality: the durable address, never an observation handle. `paramsSource` only
 * decides how a `$` token in a param reads, so it counts only where a param carries one. */
export function sameTarget(a: ComponentTarget | undefined, b: ComponentTarget | undefined): boolean {
  if (!a || !b) return a === b
  return storedAddress(a) === storedAddress(b)
}
// Serialized with sorted keys: this module also runs in the browser bundle, which has no `node:util`.
function storedAddress(target: ComponentTarget): string {
  const { paramsSource, ...durable } = durableTarget(target)
  const address = paramsSource && JSON.stringify(durable.path ?? []).includes('$') ? { ...durable, paramsSource } : durable
  return JSON.stringify(address, (_key, value: unknown) => value && typeof value === 'object' && !Array.isArray(value)
    ? Object.fromEntries(Object.entries(value).sort(([x], [y]) => x < y ? -1 : x > y ? 1 : 0)) : value)
}
const INTERACTIONS = new Set(['click', 'hover', 'mousemove', 'type', 'input', 'select', 'drag', 'dragTo', 'uploadFile', 'dropFile'])
interface TargetedAction {
  type: string; action: string; condition?: JourneyCondition; keyboardEvent?: string; selector?: string; label?: string; surfaceId?: string; controlId?: string; controlName?: string; component?: ComponentTarget; destination?: ComponentTarget; value?: string
}
/** A component target, or a recorded surfaceId+controlId pair. */
export function carriesComponentIdentity(action: Pick<TargetedAction, 'component' | 'surfaceId' | 'controlId'>): boolean {
  return !!action.component || !!(action.surfaceId && action.controlId)
}
export function needsComponent(action: TargetedAction): boolean {
  return INTERACTIONS.has(action.type) || (action.type === 'read_variable' && !!(action.selector || action.component)) || (action.type === 'pressKey' && action.keyboardEvent !== 'keyup' && !!(action.selector || action.component))
}

/** Convert an interaction the Sightmap cannot identify into a live-resolved abstract step, keeping its
 * recorded selector as the author-time witness. An abstract step never executes that selector — the
 * live resolver owns control choice — but the runner's authored-target guard reads it to reject a
 * substitute control on any page the recorded control never lived on, so dropping it would leave the
 * resolver free to click a lookalike on an unrelated surface. Strip only the identity evidence the map
 * could not verify (component, control and Surface identities, and the observation-local label). */
export function resolveAsAbstract<T extends TargetedAction>(action: T): T {
  const { component: _component, surfaceId: _surfaceId, controlId: _controlId, controlName: _controlName, label: _label, ...rest } = action
  return { ...rest, type: 'abstract' } as T
}

/** Read compatibility for observation-local identities written by older runners. A retained
 * selector identifies a control relative to its surviving owner. Without that evidence, resolve
 * the original action live; never guess a selector from the temporary ID. Fresh authoring still
 * goes directly through bindToolComponent/durableTarget and rejects these IDs. */
export function recoverLegacyObservationTarget<T extends TargetedAction>(action: T): T {
  const target = action.component
  const temporary = (id: string) => /^observed-(?:control|text)-/.test(id)
  const index = target?.path?.findIndex(part => temporary(part.componentId)) ?? -1
  const oldFlat = temporary(target?.controlId ?? action.controlId ?? '')
  if (index < 0 && !oldFlat) return action
  const selector = target?.selector
  if (target?.path && index > 0 && selector) {
    return { ...action, component: { ...target, path: target.path.slice(0, index), selector,
      observationRef: undefined } }
  }
  if (needsComponent(action)) return resolveAsAbstract(action)
  return action
}

/** Convert authoring evidence into an identity, never persist an unbound selector as an interaction. */
export function bindToolComponent<T extends TargetedAction>(action: T, sightmap?: SightmapDoc): T {
  const bound = bindActionComponent(action, sightmap)
  if (!action.condition) return bound
  const bind = (condition: JourneyCondition): JourneyCondition => {
    if (condition.type === 'route') return condition
    if (condition.type === 'not') return { ...condition, condition: bind(condition.condition) }
    if ('conditions' in condition) return { ...condition, conditions: condition.conditions.map(bind) }
    const component = condition.type === 'component'
      ? sightmap ? canonicalTarget(condition.component, sightmap) : condition.component
      : condition.selector === action.selector && bound.component?.path
        ? bound.component
        : bindActionComponent<TargetedAction>({ type: 'click', action: 'Bind condition target', selector: condition.selector }, sightmap).component
    if (!component) throw new Error('Condition requires a Sightmap target')
    return AUTHORED_CONDITION.parse({ type: 'component', operator: condition.operator, component: durableTarget(component) })
  }
  return { ...bound, condition: bind(action.condition) }
}

function bindActionComponent<T extends TargetedAction>(action: T, sightmap?: SightmapDoc): T {
  if (action.component) action = { ...action, component: durableTarget(action.component) }
  if ('checks' in action && Array.isArray(action.checks)) {
    action = { ...action, checks: action.checks.map(check => {
      if (check && typeof check === 'object' && 'component' in check && check.component) {
        const { selector: _selector, label: _label, ...rest } = check
        return sightmap ? { ...rest, component: durableTarget(canonicalTarget(COMPONENT_TARGET.parse(check.component), sightmap)) } : { ...rest, component: durableTarget(COMPONENT_TARGET.parse(check.component)) }
      }
      return check
    }) }
  }
  if (action.destination) {
    const { value: _value, ...rest } = action
    action = { ...rest, destination: durableTarget(sightmap ? canonicalTarget(action.destination, sightmap) : action.destination) } as T
  }
  if (!needsComponent(action)) return action
  if (action.component && sightmap) action = { ...action, component: canonicalTarget(action.component, sightmap) }
  if (action.component?.path) {
    const { selector: _selector, label: _label, controlId: _controlId, ...rest } = action
    return { ...rest, component: durableTarget(action.component), surfaceId: action.component.surfaceId } as T
  }
  if (action.component && !sightmap) return { ...action, surfaceId: action.component.surfaceId, controlId: action.component.controlId }
  if (!sightmap && action.surfaceId && action.controlId) return { ...action, component: { surfaceId: action.surfaceId, controlId: action.controlId } }
  const matches = sightmap?.views.flatMap(view => flattenComponents(view.components).flatMap(component => {
    const selectors = Array.isArray(component.selector) ? component.selector : [component.selector]
    return view.id && component.id && (!action.surfaceId || action.surfaceId === view.id)
      && (action.controlId ? action.controlId === component.id : !!action.selector && selectors.includes(action.selector))
      ? [{ surfaceId: view.id, controlId: component.id, controlName: component.name }] : []
  })) ?? []
  if (matches.length !== 1) throw new Error(`Cannot author tool action "${action.action}": target must identify exactly one Sightmap component (found ${matches.length}).`)
  const match = matches[0]!
  const paths = componentPaths(sightmap!, match.surfaceId).filter(path => path.at(-1)?.id === match.controlId)
  if (paths.length !== 1 || paths[0]!.some(part => part.params?.length || !part.id)) throw new Error('Target requires observed Sightmap hierarchy and instance parameters')
  const { selector: _selector, label: _label, ...rest } = action
  return { ...rest, surfaceId: match.surfaceId, component: { surfaceId: match.surfaceId, path: paths[0]!.map(part => ({ componentId: part.id! })) } } as T
}

/** Fresh element checks must already name the observed Sightmap instance. Historical catalogs
 * remain readable; this boundary applies before new authoring is judged or persisted. */
export function bindAuthoredCheck<T extends string | JourneyCheck>(check: T): T {
  if (typeof check === 'string' || (check.assertion === 'abstract' || check.assertion === 'reachSurfaceCheck') || check.assertion === 'url_contains') return check
  const structured: JourneyCheck = check
  const target = SIGHTMAP_TARGET.safeParse(structured.component)
  if (!target.success) throw new Error(`Cannot author check "${check.description}": an element check needs a Sightmap hierarchy and instance parameters, not a selector or control ID`)
  const { selector: _selector, label: _label, surfaceId: _surfaceId, controlId: _controlId, ...rest } = structured
  return { ...rest, component: target.data } as T
}

/** The authoring boundary for one new action: an authored condition and Sightmap-bound checks. */
export function bindAuthoredAction<T extends { condition?: unknown; checks: Array<string | JourneyCheck> }>(action: T): T {
  return { ...action, ...(action.condition ? { condition: AUTHORED_CONDITION.parse(action.condition) } : {}), checks: action.checks.map(bindAuthoredCheck) }
}
